// src/pages/MyStuffPage.tsx
import React, { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { BookHeart, BookOpen, Brain, Calendar, Eye, EyeOff, Trash2, Edit3, ChevronLeft, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { useSessionStore } from '@/stores/sessionStore'
import { analytics } from '@/services/analytics'

interface SavedReflection {
  id: string
  storyId: string
  storyTitle: string
  reflectionDate: string
  promptType: string
  response: string
  mood: string
  customTitle?: string
  sharedWithParent: boolean
}

export default function MyStuffPage() {
  const [, setLocation] = useLocation()
  const [reflections, setReflections] = useState<SavedReflection[]>([])
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [progress, setProgress] = useState<any>(null)

  useEffect(() => {
    loadReflections()
    loadProgress()
  }, [])

  const loadReflections = () => {
    const saved = localStorage.getItem('my-reflections')
    if (saved) {
      setReflections(JSON.parse(saved))
    }
  }

  const loadProgress = async () => {
    // Get child session from localStorage (correct key is 'child-session')
    const sessionData = localStorage.getItem('child-session')
    
    if (!sessionData) {
      console.warn('⚠️ No child session found')
      setProgress(null)
      return
    }
    
    try {
      const session = JSON.parse(sessionData)
      const childId = session.childId
      
      console.log('👤 Loading progress for child:', childId)
      
      // CRITICAL: Set the child ID in analytics before calling getProgress
      analytics.setChild(childId)
      
      const data = await analytics.getProgress()
      console.log('📊 Progress data loaded:', data)
      setProgress(data)
      
    } catch (error) {
      console.error('❌ Failed to load progress:', error)
      setProgress(null)
    }
  }

  const handleDelete = (id: string) => {
    const updated = reflections.filter(r => r.id !== id)
    setReflections(updated)
    localStorage.setItem('my-reflections', JSON.stringify(updated))
  }

  const handleKeepBuilding = (reflection: SavedReflection) => {
    // Pre-fill create page with this reflection
    localStorage.setItem('create-page-prefill', JSON.stringify({
      response: reflection.response,
      promptType: reflection.promptType
    }))
    setLocation('/create')
  }

  const getStreakCalendar = () => {
    if (!progress) return []
    
    const days = []
    const today = new Date()
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today)
      date.setDate(date.getDate() - i)
      days.push({
        date: date.toISOString().split('T')[0],
        active: date.toISOString().split('T')[0] <= progress.lastActive
      })
    }
    return days
  }

  const generateLearningNarrative = () => {
    if (!progress || progress.totalSessions === 0) return null

    const totalSessions = progress.totalSessions
    const weekSessions = progress.weekSessions || 0
    
    const narratives = []
    
    // Opening celebration - focus on ACTIVITIES not minutes
    if (totalSessions >= 10) {
      narratives.push(`You've completed ${totalSessions} learning activities! You're building serious reading power! 💪`)
    } else if (totalSessions >= 5) {
      narratives.push(`${totalSessions} activities done! You're on a roll!`)
    } else {
      narratives.push(`You're getting started! ${totalSessions} ${totalSessions === 1 ? 'activity' : 'activities'} completed.`)
    }
    
    // This week's effort (only if they did stuff this week)
    if (weekSessions > 0 && weekSessions !== totalSessions) {
      // Only mention "this week" if it's different from total
      if (weekSessions >= 5) {
        narratives.push(`This week you've been super active - ${weekSessions} activities! 🔥`)
      } else {
        narratives.push(`${weekSessions} ${weekSessions === 1 ? 'activity' : 'activities'} this week!`)
      }
    }
    
    // Skills celebration
    if (progress.strengths && progress.strengths.length > 0) {
      const topSkill = progress.strengths[0].replace(/_/g, ' ')
      narratives.push(`🌟 You're getting really comfortable with ${topSkill}!`)
    }
    
    return narratives
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => setLocation('/practice-reading')}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </Button>
        </div>

        <div className="text-center mb-8">
          <BookHeart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1F2937] mb-2">
            My Stuff
          </h1>
          <p className="text-lg text-[#6B7280]">
            All the amazing things you've created and learned
          </p>
        </div>

        {/* My Learning Journey - Narrative First */}
        {progress && progress.totalMinutes > 0 && (
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Sparkles className="w-6 h-6" />
                My Learning Journey
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {generateLearningNarrative()?.map((paragraph, idx) => (
                  <p key={idx} className="text-gray-700 leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
              
              {/* Show week activity count (not days) */}
              {progress.weekSessions > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-purple-700 font-medium">
                      This week:
                    </p>
                    <p className="text-lg font-bold text-purple-900">
                      {progress.weekSessions} {progress.weekSessions === 1 ? 'session' : 'sessions'}
                    </p>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    Keep it up! Every session builds your skills. 💪
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stories I've Thought About */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              Stories I've Thought About
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reflections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-4">You haven't saved any reflections yet!</p>
                <p className="text-sm">After reading a story and sharing your thoughts,
                  you can save them here to look back on later.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reflections.map((reflection) => (
                  <div
                    key={reflection.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {reflection.customTitle || reflection.storyTitle}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {new Date(reflection.reflectionDate).toLocaleDateString()} • 
                          Feeling {reflection.mood}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {reflection.sharedWithParent ? (
                          <Eye className="w-4 h-4 text-green-600" title="Shared with parent" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-gray-400" title="Private" />
                        )}
                      </div>
                    </div>

                    {expandedId === reflection.id ? (
                      <div className="mt-3 space-y-3">
                        <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                          {reflection.response}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleKeepBuilding(reflection)}
                            className="flex items-center gap-1"
                          >
                            <Edit3 className="w-4 h-4" />
                            Keep Building This
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(null)}
                          >
                            Hide
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(reflection.id)}
                            className="text-red-600 hover:text-red-700 ml-auto"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpandedId(reflection.id)}
                        className="mt-2"
                      >
                        Read Full Reflection
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Skills I've Practiced */}
        {progress && progress.totalSessions > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-6 h-6 text-green-600" />
                What I've Been Working On
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Things I'm getting good at */}
                {progress.strengths && progress.strengths.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-green-700 mb-2">
                      Things I'm Getting Good At:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {progress.strengths.slice(0, 3).map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200"
                        >
                          ✓ {skill.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Things I'm practicing */}
                {progress.practicingAreas && progress.practicingAreas.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-blue-700 mb-2">
                      Things I'm Practicing:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {progress.practicingAreas.slice(0, 3).map((skill) => (
                        <span
                          key={skill}
                          className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-200"
                        >
                          → {skill.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Activity count - contextual */}
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">
                      {progress.totalSessions}
                    </span>{' '}
                    learning sessions completed •{' '}
                    <span className="font-semibold text-gray-900">
                      {progress.weekSessions}
                    </span>{' '}
                    this week
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}